import { notifyApiMutation } from './mutation-events.ts';
import { getAccessToken, refreshSession, notifyLoggedOut } from './session.ts';

const API_BASE = '/api';

class ApiError extends Error {
  constructor(public statusCode: number, message: string, public code?: string) {
    super(message);
    this.name = 'ApiError';
  }
}

interface ApiOptions extends RequestInit {
  /** false = public endpoint (login, register…): no token, no refresh-and-retry. */
  auth?: boolean;
}

async function apiFetch<T>(path: string, opts?: ApiOptions): Promise<T> {
  const { auth = true, ...init } = opts ?? {};
  // Fastify rejects a JSON content-type without a body, so only declare it when sending one
  const headers = (token: string | null): Record<string, string> => ({
    ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(init.headers as Record<string, string> ?? {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  });

  let res = await fetch(`${API_BASE}${path}`, { ...init, credentials: 'same-origin', headers: headers(auth ? await getAccessToken() : null) });
  if (auth && res.status === 401) {
    // The access token may have been revoked/expired early: one refresh, one retry
    const fresh = await refreshSession();
    if (!fresh) {
      notifyLoggedOut();
    } else {
      res = await fetch(`${API_BASE}${path}`, { ...init, credentials: 'same-origin', headers: headers(fresh.token) });
      if (res.status === 401) notifyLoggedOut();
    }
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({ message: res.statusText })) as { message?: string; code?: string };
    lastApiErrorCode = `${res.status}${body.code ? ` ${body.code}` : ''}`.slice(0, 60);
    throw new ApiError(res.status, body.message ?? res.statusText, body.code);
  }

  notifyApiMutation(init.method, path);
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/** M10.6: last API error (status + code only) — attached to bug reports, nothing else is kept. */
let lastApiErrorCode: string | null = null;
export const getLastApiErrorCode = () => lastApiErrorCode;

export { apiFetch, ApiError };
